import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip,
  Paper,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Collapse,
} from '@mui/material';
import {
  Info as InfoIcon,
  Warning as WarnIcon,
  Error as ErrorIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useLogger } from '../hooks/useLogger';
import { LogEntry } from '../types';
import { DEBUG_COLORS } from '../config';
import { useNotification } from '../context/NotificationContext';

export const Logger: React.FC = () => {
  const { updateCount } = useNotification();
  const {
    logs,
    isPaused,
    setIsPaused,
    clearLogs,
    exportLogs,
    copyLog,
  } = useLogger();

  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (levelFilter !== 'all' && log.level !== levelFilter) {
        return false;
      }
      if (searchQuery && !log.message.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [logs, levelFilter, searchQuery]);

  // Update notification count when logs change
  useEffect(() => {
    updateCount('logger', logs.length);
  }, [logs.length, updateCount]);

  const getLogIcon = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return <ErrorIcon sx={{ fontSize: 16, color: DEBUG_COLORS.error }} />;
      case 'warn':
        return <WarnIcon sx={{ fontSize: 16, color: DEBUG_COLORS.warning }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 16, color: DEBUG_COLORS.info }} />;
      default:
        return <InfoIcon sx={{ fontSize: 16, color: DEBUG_COLORS.info }} />;
    }
  };

  const getLogColor = (level: LogEntry['level']): string => {
    switch (level) {
      case 'error':
        return DEBUG_COLORS.error;
      case 'warn':
        return DEBUG_COLORS.warning;
      case 'info':
        return DEBUG_COLORS.info;
      default:
        return DEBUG_COLORS.neutral;
    }
  };

  const toggleExpand = (logId: string) => {
    setExpandedLogs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <IconButton
          size="small"
          onClick={() => setIsPaused(!isPaused)}
          color={isPaused ? 'default' : 'primary'}
        >
          {isPaused ? <PlayIcon /> : <PauseIcon />}
        </IconButton>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Level</InputLabel>
          <Select
            value={levelFilter}
            label="Level"
            onChange={(e) => setLevelFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="log">Log</MenuItem>
            <MenuItem value="info">Info</MenuItem>
            <MenuItem value="warn">Warn</MenuItem>
            <MenuItem value="error">Error</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search logs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
      </Box>

      {/* Log List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 400, overflow: 'auto' }}>
        {filteredLogs.length > 0 ? (
          filteredLogs.map((log) => {
            const isExpanded = expandedLogs.has(log.id);
            return (
              <Paper
                key={log.id}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  borderLeft: `3px solid ${getLogColor(log.level)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  {getLogIcon(log.level)}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(log.timestamp).toLocaleTimeString()}
                      </Typography>
                      {log.source && (
                        <Chip
                          label={log.source}
                          size="small"
                          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', height: 20 }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          wordBreak: 'break-word',
                        }}
                      >
                        {log.message}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyLog(log)}
                        sx={{ p: 0.5 }}
                        title="Copy log"
                      >
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {(log.data || log.stack) && (
                      <Box>
                        <Button
                          size="small"
                          onClick={() => toggleExpand(log.id)}
                          sx={{ textTransform: 'none', fontSize: '0.7rem' }}
                        >
                          {isExpanded ? '▼' : '▶'} {log.stack ? 'View Stack Trace' : 'View Data'}
                        </Button>
                        <Collapse in={isExpanded}>
                          <Box
                            sx={{
                              mt: 1,
                              p: 1,
                              bgcolor: 'background.default',
                              borderRadius: 0.5,
                              fontFamily: 'monospace',
                              fontSize: '0.75rem',
                              maxHeight: 150,
                              overflow: 'auto',
                            }}
                          >
                            {log.stack ? (
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{log.stack}</pre>
                            ) : (
                              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(log.data, null, 2)}
                              </pre>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', p: 2, textAlign: 'center' }}>
            {isPaused ? 'Logging is paused' : 'No logs captured'}
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          ─── {filteredLogs.length} logs total ───
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={exportLogs}
            disabled={logs.length === 0}
          >
            Export
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={clearLogs}
            disabled={logs.length === 0}
          >
            Clear
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

